import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { InvitePage } from '@/features/auth/InvitePage';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from '@/features/auth/PasswordResetPages';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { ComingSoon } from '@/features/placeholder/ComingSoon';
import { NotFound } from '@/features/placeholder/NotFound';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ProjectsPage } from '@/features/projects/ProjectsPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { TagsPage } from '@/features/tags/TagsPage';
import { TeamPage } from '@/features/team/TeamPage';
import { TrackerPage } from '@/features/tracker/TrackerPage';
import { AppLayout } from './AppLayout';
import { allNavItems } from './navigation';
import { WorkspaceProvider } from './WorkspaceProvider';

const sharedReportPages = () => import('@/features/reports/SharedReportPages');
const reportsPage = () => import('@/features/reports/ReportsPage').then((m) => ({ Component: m.ReportsPage }));

/** Fertige Seiten; alle übrigen Navigationspunkte zeigen einen Platzhalter mit Phase. */
const pages: Record<string, RouteObject[]> = {
  tracker: [{ path: 'tracker', element: <TrackerPage /> }],
  timesheet: [{ path: 'timesheet', lazy: () => import('@/features/timesheet/TimesheetPage').then((m) => ({ Component: m.TimesheetPage })) }],
  approvals: [
    { path: 'approvals', lazy: () => import('@/features/approvals/ApprovalsPage').then((m) => ({ Component: m.ApprovalsPage })) },
    { path: 'approvals/:tab', lazy: () => import('@/features/approvals/ApprovalsPage').then((m) => ({ Component: m.ApprovalsPage })) },
  ],
  timeOff: [
    { path: 'time-off', lazy: () => import('@/features/timeoff/TimeOffPage').then((m) => ({ Component: m.TimeOffPage })) },
    { path: 'time-off/:tab', lazy: () => import('@/features/timeoff/TimeOffPage').then((m) => ({ Component: m.TimeOffPage })) },
  ],
  invoices: [
    { path: 'invoices', lazy: () => import('@/features/invoices/InvoicesPage').then((m) => ({ Component: m.InvoicesPage })) },
    { path: 'invoices/:invoiceId', lazy: () => import('@/features/invoices/InvoiceDetailPage').then((m) => ({ Component: m.InvoiceDetailPage })) },
  ],
  kiosks: [
    { path: 'kiosks', lazy: () => import('@/features/kiosk/KiosksPage').then((m) => ({ Component: m.KiosksPage })) },
    { path: 'kiosks/:tab', lazy: () => import('@/features/kiosk/KiosksPage').then((m) => ({ Component: m.KiosksPage })) },
  ],
  expenses: [
    { path: 'expenses', lazy: () => import('@/features/expenses/ExpensesPage').then((m) => ({ Component: m.ExpensesPage })) },
    { path: 'expenses/:tab', lazy: () => import('@/features/expenses/ExpensesPage').then((m) => ({ Component: m.ExpensesPage })) },
  ],
  schedule: [
    { path: 'schedule', lazy: () => import('@/features/schedule/SchedulePage').then((m) => ({ Component: m.SchedulePage })) },
    { path: 'schedule/:tab', lazy: () => import('@/features/schedule/SchedulePage').then((m) => ({ Component: m.SchedulePage })) },
  ],
  activity: [{ path: 'activity', lazy: () => import('@/features/activity/ActivityPage').then((m) => ({ Component: m.ActivityPage })) }],
  calendar: [{ path: 'calendar', lazy: () => import('@/features/calendar/CalendarPage').then((m) => ({ Component: m.CalendarPage })) }],
  // Seiten mit Diagrammen werden nachgeladen (Code-Splitting)
  dashboard: [{ path: 'dashboard', lazy: () => import('@/features/dashboard/DashboardPage').then((m) => ({ Component: m.DashboardPage })) }],
  reports: [
    { path: 'reports', lazy: reportsPage },
    { path: 'reports/:tab', lazy: reportsPage },
    { path: 'reports/shared/:id', lazy: () => sharedReportPages().then((m) => ({ Component: m.WorkspaceSharedReportPage })) },
  ],
  projects: [
    { path: 'projects', element: <ProjectsPage /> },
    {
      path: 'projects/:projectId',
      lazy: () => import('@/features/projects/ProjectDetailPage').then((m) => ({ Component: m.ProjectDetailPage })),
    },
  ],
  team: [{ path: 'team', element: <TeamPage /> }],
  clients: [{ path: 'clients', element: <ClientsPage /> }],
  tags: [{ path: 'tags', element: <TagsPage /> }],
  settings: [{ path: 'settings', element: <SettingsPage /> }, { path: 'settings/:tab', element: <SettingsPage /> }],
  billing: [{ path: 'billing', lazy: () => import('@/features/billing/BillingPage').then((m) => ({ Component: m.BillingPage })) }],
};

export const appRoutes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/invite/:token', element: <InvitePage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/kiosk/:token', lazy: () => import('@/features/kiosk/KioskTerminalPage').then((m) => ({ Component: m.KioskTerminalPage })) },
  { path: '/shared/:token', lazy: () => sharedReportPages().then((m) => ({ Component: m.PublicReportPage })) },
  {
    path: '/',
    element: (
      <WorkspaceProvider>
        <AppLayout />
      </WorkspaceProvider>
    ),
    children: [
      { index: true, element: <Navigate to="/tracker" replace /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'admin', lazy: () => import('@/features/admin/AdminPage').then((m) => ({ Component: m.AdminPage })) },
      { path: 'admin/:tab', lazy: () => import('@/features/admin/AdminPage').then((m) => ({ Component: m.AdminPage })) },
      ...allNavItems.flatMap(
        (item) => pages[item.key] ?? [{ path: item.path.slice(1), element: <ComingSoon item={item} /> }],
      ),
      { path: '*', element: <NotFound /> },
    ],
  },
];

export const router = createBrowserRouter(appRoutes);
